import React from 'react';
import { LanguageSelector } from '../components/LanguageSelector';
import { TRANSLATIONS } from '../constants';
import { LanguageCode } from '../types';
import { Activity, Sparkles, MessageCircle, Phone } from 'lucide-react';

export const HomePage: React.FC = () => {
  const t = TRANSLATIONS[LanguageCode.EN];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden bg-neutral-950">

      {/* Background ambient glow */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[15%] w-[500px] h-[500px] bg-brand-900/20 rounded-full blur-[130px]" />
        <div className="absolute bottom-[-10%] right-[15%] w-[600px] h-[600px] bg-brand-800/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col items-center">

        {/* Brand header */}
        <div className="mb-14 flex items-center gap-3">
          <div className="p-2 border border-brand-500/30 rounded-lg bg-brand-900/10 backdrop-blur-sm">
            <Activity className="w-6 h-6 text-brand-400" />
          </div>
          <span className="text-xl font-bold tracking-wide text-white font-sans">
            Sister<span className="text-brand-400">AI</span>
          </span>
        </div>

        <div className="mb-14 text-center max-w-4xl mx-auto">
          {/* Pill tag */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 bg-brand-900/20 backdrop-blur-md mb-8">
            <Sparkles className="w-3 h-3 text-brand-400" />
            <span className="text-xs font-medium text-brand-200 uppercase tracking-widest">
              Powered by Transformative AI
            </span>
          </div>

          {/* Main title */}
          <h1 className="text-5xl md:text-7xl font-serif font-medium text-white mb-6 leading-tight">
            Health Guidance for <br />
            <span className="italic text-brand-400">Every South African.</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
            AI-powered nurse triage in all 6 major South African languages — with clinical guidance, Ubuntu health wisdom, and voice support.
          </p>
        </div>

        {/* Emergency banner */}
        <div className="mb-10 flex items-center gap-3 px-5 py-3 bg-red-950/40 border border-red-700/40 rounded-xl text-sm text-red-300">
          <Phone className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Emergency:</strong> 112 (General) &nbsp;|&nbsp; 10177 (Ambulance) &nbsp;|&nbsp; SADAG 0800 567 567 (Mental Health)
          </span>
        </div>

        {/* Language selector */}
        <div className="w-full flex flex-col items-center mb-20">
          <h2 className="text-xs font-semibold text-brand-500 uppercase tracking-[0.2em] mb-8 border-b border-brand-900/50 pb-2">
            {t.selectLanguage}
          </h2>
          <LanguageSelector />
        </div>

        {/* Footer */}
        <footer className="w-full border-t border-white/5 pt-8 pb-8 text-center flex flex-col items-center gap-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs text-gray-500 font-medium tracking-wider uppercase">
            <a href="https://intraguard-production.up.railway.app" target="_blank" rel="noopener noreferrer" className="hover:text-brand-400 transition-colors">IntraGuard</a>
            <a href="https://del.echoaistudio.xyz" target="_blank" rel="noopener noreferrer" className="hover:text-brand-400 transition-colors">Del</a>
            <a href="https://jurisafrika-375398011460.us-west1.run.app" target="_blank" rel="noopener noreferrer" className="hover:text-brand-400 transition-colors">JurisAfrika</a>
            <a href="https://echoaistudio.xyz" target="_blank" rel="noopener noreferrer" className="hover:text-brand-400 transition-colors">echoaistudio.xyz</a>
          </div>

          <a
            href="https://wa.me/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-green-900/20 hover:bg-green-900/30 border border-green-800/50 rounded-full transition-all group"
          >
            <MessageCircle className="w-4 h-4 text-green-500 group-hover:text-green-400" />
            <span className="text-sm font-medium text-green-500 group-hover:text-green-400">
              Chat on WhatsApp
            </span>
          </a>

          <p className="text-gray-600 text-xs">
            &copy; {new Date().getFullYear()} SisterAI. A product by EchoAI Studio.
          </p>
        </footer>
      </div>
    </div>
  );
};
